import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { recordOpeningBalance } from '@/services/accountService';
import { fetchFinancialAccounts, FinancialAccount } from '@/services/financialService';
import { toast } from 'sonner';

interface SetOpeningBalancesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function SetOpeningBalancesDialog({
  open,
  onOpenChange,
  onSuccess
}: SetOpeningBalancesDialogProps) {
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadAccounts();
    }
  }, [open]);

  const loadAccounts = async () => {
    try {
      const data = await fetchFinancialAccounts();
      setAccounts(data);
      
      // Initialize balances
      const initialBalances: Record<string, number> = {};
      data.forEach(account => {
        initialBalances[account.id] = account.current_balance || 0;
      });
      setBalances(initialBalances);
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error('فشل تحميل الحسابات / Failed to load accounts');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      // Record opening balance for each account
      const promises = Object.entries(balances).map(([accountId, balance]) => {
        if (balance !== 0) {
          return recordOpeningBalance(accountId, balance);
        }
        return Promise.resolve();
      });

      await Promise.all(promises);

      toast.success('تم تسجيل الأرصدة الافتتاحية بنجاح / Opening balances recorded successfully');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error recording opening balances:', error);
      const errorMessage = error instanceof Error ? error.message : 'فشل تسجيل الأرصدة / Failed to record balances';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-EG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' EGP';
  };

  const groupedAccounts = {
    asset: accounts.filter(a => a.account_type === 'asset'),
    liability: accounts.filter(a => a.account_type === 'liability'),
    equity: accounts.filter(a => a.account_type === 'equity')
  };

  const totalAssets = Object.entries(balances)
    .filter(([id]) => accounts.find(a => a.id === id)?.account_type === 'asset')
    .reduce((sum, [, balance]) => sum + balance, 0);

  const totalLiabilities = Object.entries(balances)
    .filter(([id]) => accounts.find(a => a.id === id)?.account_type === 'liability')
    .reduce((sum, [, balance]) => sum + balance, 0);

  const totalEquity = Object.entries(balances)
    .filter(([id]) => accounts.find(a => a.id === id)?.account_type === 'equity')
    .reduce((sum, [, balance]) => sum + balance, 0);

  const isBalanced = Math.abs(totalAssets - (totalLiabilities + totalEquity)) < 0.01;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            تسجيل الأرصدة الافتتاحية / Set Opening Balances
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            قم بإدخال الأرصدة الابتدائية لجميع الحسابات / Enter starting balances for all accounts
          </p>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Assets */}
          <div className="space-y-3">
            <h4 className="font-semibold text-blue-600">الأصول / Assets</h4>
            {groupedAccounts.asset.map((account) => (
              <div key={account.id} className="flex items-center gap-4">
                <Label className="flex-1">{account.account_name}</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="w-40"
                  value={balances[account.id] || 0}
                  onChange={(e) => setBalances({
                    ...balances,
                    [account.id]: parseFloat(e.target.value) || 0
                  })}
                  placeholder="0.00"
                />
              </div>
            ))}
            <div className="flex justify-between pt-2 border-t font-semibold text-blue-600">
              <span>إجمالي الأصول / Total Assets:</span>
              <span>{formatCurrency(totalAssets)}</span>
            </div>
          </div>

          {/* Liabilities */}
          <div className="space-y-3">
            <h4 className="font-semibold text-orange-600">الالتزامات / Liabilities</h4>
            {groupedAccounts.liability.map((account) => (
              <div key={account.id} className="flex items-center gap-4">
                <Label className="flex-1">{account.account_name}</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="w-40"
                  value={balances[account.id] || 0}
                  onChange={(e) => setBalances({
                    ...balances,
                    [account.id]: parseFloat(e.target.value) || 0
                  })}
                  placeholder="0.00"
                />
              </div>
            ))}
            <div className="flex justify-between pt-2 border-t font-semibold text-orange-600">
              <span>إجمالي الالتزامات / Total Liabilities:</span>
              <span>{formatCurrency(totalLiabilities)}</span>
            </div>
          </div>

          {/* Equity */}
          <div className="space-y-3">
            <h4 className="font-semibold text-green-600">حقوق الملكية / Equity</h4>
            {groupedAccounts.equity.map((account) => (
              <div key={account.id} className="flex items-center gap-4">
                <Label className="flex-1">{account.account_name}</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="w-40"
                  value={balances[account.id] || 0}
                  onChange={(e) => setBalances({
                    ...balances,
                    [account.id]: parseFloat(e.target.value) || 0
                  })}
                  placeholder="0.00"
                />
              </div>
            ))}
            <div className="flex justify-between pt-2 border-t font-semibold text-green-600">
              <span>إجمالي حقوق الملكية / Total Equity:</span>
              <span>{formatCurrency(totalEquity)}</span>
            </div>
          </div>

          {/* Balance Check */}
          <div className={`p-4 rounded-lg ${isBalanced ? 'bg-green-100 border border-green-400' : 'bg-red-100 border border-red-400'}`}>
            <p className={`font-semibold ${isBalanced ? 'text-green-800' : 'text-red-800'}`}>
              {isBalanced 
                ? '✓ متوازن / Balanced (Assets = Liabilities + Equity)' 
                : '⚠ غير متوازن / Not Balanced - Please adjust'}
            </p>
            <div className="mt-2 text-sm">
              <p>الأصول / Assets: {formatCurrency(totalAssets)}</p>
              <p>الالتزامات + حقوق الملكية / Liabilities + Equity: {formatCurrency(totalLiabilities + totalEquity)}</p>
              <p className="font-semibold">
                الفرق / Difference: {formatCurrency(Math.abs(totalAssets - (totalLiabilities + totalEquity)))}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              إلغاء / Cancel
            </Button>
            <Button type="submit" disabled={loading || !isBalanced}>
              {loading ? 'جاري الحفظ... / Saving...' : 'حفظ / Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
